/**
 * DailyInsightHero — Homly V3
 * ============================
 * Bloc hero quotidien affiché en haut du dashboard manager.
 * Présente UN insight Claude unique par jour ("Homly te dit quelque chose de nouveau").
 *
 * - Cache 24h côté backend
 * - Bouton "Régénérer" 1x/jour
 * - Mood-aware (couleur selon urgent/attention/neutral/positive)
 */
import { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Sparkles, RefreshCw, AlertCircle, TrendingUp, Info, Flame } from 'lucide-react';

const MOOD_STYLES = {
    urgent: {
        bg: 'bg-gradient-to-br from-red-50 via-orange-50 to-white',
        border: 'border-red-200',
        accent: 'text-red-700',
        icon: Flame,
        iconBg: 'bg-red-100 text-red-700',
        chip: 'bg-red-100 text-red-700',
        label: 'ACTION IMMÉDIATE',
    },
    attention: {
        bg: 'bg-gradient-to-br from-amber-50 via-orange-50 to-white',
        border: 'border-amber-200',
        accent: 'text-amber-700',
        icon: AlertCircle,
        iconBg: 'bg-amber-100 text-amber-700',
        chip: 'bg-amber-100 text-amber-700',
        label: 'À SURVEILLER',
    },
    positive: {
        bg: 'bg-gradient-to-br from-emerald-50 via-green-50 to-white',
        border: 'border-emerald-200',
        accent: 'text-emerald-700',
        icon: TrendingUp,
        iconBg: 'bg-emerald-100 text-emerald-700',
        chip: 'bg-emerald-100 text-emerald-700',
        label: 'BONNE NOUVELLE',
    },
    neutral: {
        bg: 'bg-gradient-to-br from-slate-50 to-white',
        border: 'border-slate-200',
        accent: 'text-slate-700',
        icon: Info,
        iconBg: 'bg-slate-100 text-slate-600',
        chip: 'bg-slate-100 text-slate-600',
        label: 'INSIGHT DU JOUR',
    },
};

export default function DailyInsightHero() {
    const [insight, setInsight] = useState(null);
    const [loading, setLoading] = useState(true);
    const [regenerating, setRegenerating] = useState(false);
    const [error, setError] = useState(null);

    const fetchInsight = async (regenerate = false) => {
        try {
            if (regenerate) setRegenerating(true);
            else setLoading(true);
            const res = await api.get(
                `/ai/daily-insight${regenerate ? '?regenerate=true' : ''}`,
            );
            setInsight(res.data);
            setError(null);
        } catch (e) {
            setError(e?.response?.data?.detail || e.message);
        } finally {
            setLoading(false);
            setRegenerating(false);
        }
    };

    useEffect(() => {
        fetchInsight(false);
    }, []);

    if (loading) {
        return (
            <div
                className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-2xl p-6 animate-pulse"
                data-testid="daily-insight-loading"
            >
                <div className="h-5 bg-slate-200 rounded w-1/3 mb-3"></div>
                <div className="h-7 bg-slate-200 rounded w-3/4 mb-4"></div>
                <div className="h-4 bg-slate-200 rounded w-full mb-2"></div>
                <div className="h-4 bg-slate-200 rounded w-5/6"></div>
            </div>
        );
    }

    if (error || !insight) {
        return (
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 text-sm text-slate-600">
                Insight du jour temporairement indisponible.
            </div>
        );
    }

    const mood = MOOD_STYLES[insight.mood] || MOOD_STYLES.neutral;
    const Icon = mood.icon;

    return (
        <div
            className={`${mood.bg} border ${mood.border} rounded-2xl p-6 relative overflow-hidden shadow-sm`}
            data-testid="daily-insight-hero"
        >
            <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4 flex-1 min-w-0">
                    <div className={`${mood.iconBg} p-3 rounded-xl shrink-0`}>
                        <Icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                            <span className={`text-xs font-bold tracking-wider ${mood.chip} px-2 py-1 rounded-md inline-flex items-center gap-1`}>
                                <Sparkles className="w-3 h-3" /> {mood.label}
                            </span>
                            <span className="text-xs text-slate-500">Insight Pilote · {insight.day_key}</span>
                        </div>
                        <h2 className={`text-xl md:text-2xl font-bold ${mood.accent} leading-tight mb-3`} data-testid="daily-insight-headline">
                            {insight.headline}
                        </h2>
                        <p className="text-slate-700 text-sm md:text-base leading-relaxed mb-4" data-testid="daily-insight-body">
                            {insight.body}
                        </p>

                        {insight.focus_metric && insight.focus_metric.label && (
                            <div className="bg-white/70 border border-white rounded-xl p-3 mb-4 inline-flex items-center gap-3">
                                <div>
                                    <div className="text-xs text-slate-500 uppercase tracking-wider">{insight.focus_metric.label}</div>
                                    <div className="text-2xl font-bold text-slate-900 tabular-nums">
                                        {insight.focus_metric.value}
                                        <span className="text-sm font-normal text-slate-500 ml-1">
                                            {insight.focus_metric.unit === 'count' ? '' : insight.focus_metric.unit}
                                        </span>
                                    </div>
                                </div>
                                {insight.focus_metric.benchmark && (
                                    <div className="text-xs text-slate-600 border-l border-slate-300 pl-3">
                                        Benchmark : <strong>{insight.focus_metric.benchmark}</strong>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="bg-slate-900 text-white rounded-xl p-3 inline-flex items-center gap-3 max-w-full" data-testid="daily-insight-action">
                            <span className="text-xs font-bold uppercase tracking-wider text-emerald-400 shrink-0">Action du jour</span>
                            <span className="text-sm font-medium">{insight.suggested_action}</span>
                        </div>
                    </div>
                </div>
                <button
                    onClick={() => fetchInsight(true)}
                    disabled={regenerating}
                    className="text-slate-500 hover:text-slate-900 transition-colors p-2 hover:bg-white/50 rounded-lg shrink-0"
                    title="Régénérer l'insight (compte dans le quota IA)"
                    data-testid="daily-insight-regenerate"
                >
                    <RefreshCw className={`w-5 h-5 ${regenerating ? 'animate-spin' : ''}`} />
                </button>
            </div>

            {insight.from_cache === false && (
                <div className="absolute top-2 right-2 text-[10px] font-bold text-emerald-700 bg-emerald-100 px-2 py-1 rounded-full">
                    NOUVEAU
                </div>
            )}
        </div>
    );
}
